import React, { ReactElement, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { AuthDialog } from 'components/AuthDialog/AuthDialog';
import { changeNewSpot, clearAddSpot } from 'store/slices/newSpot';
import { getCategories } from 'store/slices/categories';
import { Button, ToggleButton } from '@mui/material';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import Typography from '@mui/material/Typography';
import { renderCategoryIcon, renderHazardIcon } from 'utils/categoriesAndHazards';
import Box from '@mui/material/Box';
import ErrorMessage from '../ErrorMessage';
import { useAutoSaveDraft } from 'hooks/useAutoSaveDraft';

interface CustomToggleButtonProps {
    id: string;
    isSelected: boolean;
    toggleHandler: (isSelected: boolean, id: string) => void;
    name: string;
    icon: ReactElement;
    description: string;
}

const CustomToggleButton = ({
    id,
    isSelected,
    toggleHandler,
    name,
    icon,
    description,
}: CustomToggleButtonProps) =>
    <ToggleButton
        value={id}
        selected={isSelected}
        onChange={() => toggleHandler(isSelected, id)}
        sx={{
            'width': '100%',
            'textTransform': 'none',
            'textAlign': 'left',
            'display': 'flex',
            'flexDirection': 'row',
            'alignItems': 'center',
            'gap': 1.5,
            'px': 1.5,
            'py': 1,
            'borderRadius': '8px',
            '&.Mui-selected': {
                'borderColor': 'primary.main',
                'backgroundColor': 'primary.main',
                'color': 'primary.contrastText',
                '& svg': { color: 'primary.contrastText' },
                '& .description': { color: 'primary.contrastText', opacity: 0.8 },
            },
        }}
    >
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            {icon ? React.cloneElement(icon, { sx: { fontSize: '1.4rem' } }) : null}
        </Box>
        <Box>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.2 }}>
                {name}
            </Typography>
            <Typography className='description' sx={{ fontSize: '0.7rem', lineHeight: 1.3, color: 'text.secondary', mt: 0.25 }}>
                {description}
            </Typography>
        </Box>
    </ToggleButton>

const AddSpotStep2 = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { save } = useAutoSaveDraft();
    const newSpot = useAppSelector((state) => state.newSpot);
    const categoriesData = useAppSelector((state) => state.categories);
    const { selectedCategories, selectedHazards } = newSpot;
    const { gettingCategories, gettingCategoriesSuccess, gettingCategoriesError, categories, hazards } = categoriesData;

    const checkboxSelect = (checked: boolean, idSelected: string, currentValues: Array<string>, prop: string) => {
        const cv = currentValues ?? [];
        const data = checked ? [...cv, idSelected] : cv.filter((id) => id !== idSelected);
        void dispatch(changeNewSpot({ [prop]: data }));
    };

    const handleCategoryToggle = (isSelected: boolean, categoryId: string) => {
        checkboxSelect(!isSelected, categoryId, selectedCategories, 'selectedCategories');
    };

    const handleHazardToggle = (isSelected: boolean, hazardId: string) => {
        checkboxSelect(!isSelected, hazardId, selectedHazards, 'selectedHazards');
    };

    useEffect(() => {
        if (!gettingCategoriesSuccess) {
            void dispatch(getCategories());
        }
    }, [gettingCategoriesSuccess]);

    return (
        <AuthDialog
            onClose={() => {
                save(2);
                dispatch(clearAddSpot());
                history.push('/');
            }}
            onBack={() => {
                save(2);
                history.push('/add-spot/step-1');
            }}
            title={'Create a New Spot'}
        >
            <Box>
                {gettingCategories &&
                    <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', py: 2 }}>
                        Loading categories...
                    </Typography>
                }
                {!gettingCategories && gettingCategoriesError &&
                    <ErrorMessage specificError={'Unable to load categories. You can continue without selecting any.'} />
                }
                {!gettingCategories && gettingCategoriesSuccess &&
                    <form>
                        <Typography variant='body2' color='text.secondary' mb={1}>
                            Spot type
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 3 }}>
                            {categories?.map((c) => (
                                <CustomToggleButton
                                    key={c.id}
                                    id={c.id}
                                    name={c.name}
                                    icon={renderCategoryIcon(c.shortname)}
                                    description={c.description}
                                    toggleHandler={handleCategoryToggle}
                                    isSelected={!!(selectedCategories?.find((id) => id === c.id))}
                                />
                            ))}
                        </Box>

                        <Typography variant='body2' color='text.secondary' mb={1}>
                            Hazards
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 3 }}>
                            {hazards?.map((h) => (
                                <CustomToggleButton
                                    key={h.id}
                                    id={h.id}
                                    name={h.name}
                                    icon={renderHazardIcon(h.shortname)}
                                    description={h.description}
                                    toggleHandler={handleHazardToggle}
                                    isSelected={!!(selectedHazards?.find((id) => id === h.id))}
                                />
                            ))}
                        </Box>

                        <Box sx={{ textAlign: 'center', pt: 1 }}>
                            <Button
                                variant='contained'
                                color='primary'
                                onClick={() => { save(2); history.push('/add-spot/step-3'); }}
                            >
                                Continue
                            </Button>
                            <Typography variant='body2' color='text.secondary' sx={{ mt: 1, fontSize: '.85em' }}>
                                Next: add photos &amp; video
                            </Typography>
                        </Box>
                    </form>
                }
            </Box>
        </AuthDialog>
    );
};

export default AddSpotStep2;
